import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import { Grid, Divider } from '@material-ui/core';
import dynamic from 'next/dynamic';
import ReactPlayer from 'react-player/lazy';
import EditCardButtons from '../EditCardButtons';
// import { useTranslation } from '../../../src/i18n';
import { TP, MEDIA_ATTRIBUTES_TYPES_IDS } from '../../../constants/consts';
import CardBox from '../../CardBox';
import Title from '../../Title';
// import ProfileChip from '../../ProfileChip';
import { colors } from '../../../constants/styleConstants';
import { getOption } from '../../../utils/common';
import ProductionListedCardHeaders from '../../ProductionListedCardHeaders';
import DisablingCover from '../../DisablingCover';

const SimpleModal = dynamic(import('../../Modal'));
const MediaPlayer = dynamic(import('../../MediaPlayer'));
const ImageTag = dynamic(import('../../ImageTag'));

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    position: 'relative',
  },
  infoBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  infoTextBlock: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  media: {
    maxWidth: '100%',
    height: 'auto',
  },
  titleBlock: {
    marginBottom: theme.spacing(1),
  },
  title: {
    display: 'inline-block',
    marginRight: theme.spacing(1),
  },
  BlockTitle: {
    display: 'block',
  },
  infoElementsBlock: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  moreInfo: {
    color: colors.blue,
  },
  videoContainer: {
    width: 'auto',
    minWidth: 'auto',
    minHeight: 'auto',
    height: '100%',
  },
  videoBtn: {
    display: 'flex',
    flexWrap: 'wrap',
    borderRadius: '2px',
    padding: 0,
    border: 0,
    cursor: 'pointer',
    margin: 'auto',
    width: '100%',
    minWidth: 150,
    '&:focus': {
      outline: 0,
    },
  },
  btnWrapper: {
    width: '100%',
    minWidth: '68px',
    minHeight: '68px',
    pointerEvents: 'none',
    cursor: 'pointer',
  },
  videoModal: {
    width: 'initial',
    height: 'initial',
  },
  imageBtn: {
    maxWidth: 150,
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    background: 'transparent',
    lineHeight: 0,
    '&:focus': {
      outline: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  },
  infoContainer: {
    flex: 1,
  },
  mediaContainer: {
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  },
  viewModal: {
    borderRadius: 5,
    width: 'initial',
    height: 'initial',
  },
  viewModalImg: {
    display: 'block',
    maxWidth: '90vw',
    maxHeight: '90vh',
  },
}));

const EditMediaCard = ({
  className,
  media,
  editClick,
  privacyOptions,
  mediaType,
  onPrivacyChange,
  onDelete,
  loading,
}) => {
  const classes = useStyles();
  // const { t } = useTranslation();

  const [isModalOpen, setModalOpen] = React.useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const privacyChange = v => {
    onPrivacyChange(v, media?.id);
  };

  const mediaTypeName = React.useCallback(() => {
    const typeId = mediaType === 'image' ? MEDIA_ATTRIBUTES_TYPES_IDS.IMAGE : MEDIA_ATTRIBUTES_TYPES_IDS.VIDEO;
    return (media?.attributes || []).find(attr => attr?.attributeType?.id === typeId)?.name;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);

  return (
    <CardBox className={cn(classes.root, className)}>
      <Grid container spacing={2} justify="center">
        <Grid item className={classes.mediaContainer}>
          {mediaType === 'image' ? (
            <>
              <button type="button" className={classes.imageBtn} onClick={() => openModal()}>
                <ImageTag
                  src={media?.file?.urls?.medium || media?.url || media?.file?.data?.content}
                  alt={media?.description || 'media'}
                  className={classes.media}
                />
              </button>
              {isModalOpen && (
                <SimpleModal
                  className={classes.viewModal}
                  isModalOpen={isModalOpen}
                  onClose={closeModal}
                  transparentClose
                >
                  <ImageTag
                    src={media?.file?.urls?.large || media?.url || media?.file?.data?.content}
                    alt={media?.description || 'media'}
                    className={classes.viewModalImg}
                  />
                </SimpleModal>
              )}
            </>
          ) : (
            <div className={classes.videoContainer}>
              <button
                type="button"
                className={classes.videoBtn}
                onClick={() => openModal()}
                disabled={!ReactPlayer.canPlay(media?.url || media?.file?.urls?.original)}
              >
                <MediaPlayer
                  record={media}
                  type="thumb"
                  showPlayButton
                  className={classes.btnWrapper}
                  onThumbClick={() => null}
                />
              </button>
              {isModalOpen && (
                <SimpleModal
                  isModalOpen={isModalOpen}
                  onClose={closeModal}
                  className={classes.videoModal}
                  transparentClose
                >
                  <MediaPlayer record={media} playing showPlayButton={false} selfSize showControls />
                </SimpleModal>
              )}
            </div>
          )}
        </Grid>
        <Grid item className={classes.infoContainer}>
          <div className={classes.infoBlock}>
            <div className={classes.infoTextBlock}>
              <div className={classes.titleBlock}>
                {media?.description && <Title className={classes.title}>{media?.description}</Title>}
                <Title size="small">{mediaTypeName()}</Title>
              </div>
              <div>
                {mediaType === 'video' && (
                  <Title size="small" className={classes.BlockTitle}>
                    {media?.video?.provider}
                  </Title>
                )}
              </div>
            </div>
            <EditCardButtons
              editClick={editClick}
              visibilityClick={privacyChange}
              privacyOptions={privacyOptions}
              value={getOption(media?.privacyAttribute)}
              onDelete={onDelete}
            />
          </div>
          {media?.selectedProduction && <Divider />}
          <div>
            <div className={classes.infoElementsBlock}>
              <ProductionListedCardHeaders production={media?.selectedProduction} />
            </div>
            {/* until BE provide media owner data for correct tagging and displaying */}
            {/* {media?.profileTags?.length > 0 && (
              <>
                <Title size="small">
                  {t(mediaType === 'image' ? `${TP}.FN_OTHER_ARTISTS_ON_IMAGE` : `${TP}.FN_OTHER_ARTISTS_ON_VIDEO`)}
                </Title>
                <Grid container spacing={1} alignItems="center">
                  {media?.profileTags?.slice(0, 2).map(tag => (
                    <Grid item key={tag?.id}>
                      <ProfileChip profile={tag?.profile} />
                    </Grid>
                  ))}
                  {media?.profileTags?.length > 2 && (
                    <Grid item className={classes.moreInfo}>
                      {t(`${TP}.FN_PLUS_MORE`, { number: media?.profileTags?.length - 2 })}
                    </Grid>
                  )}
                </Grid>
              </>
            )} */}
          </div>
        </Grid>
      </Grid>
      {loading && <DisablingCover />}
    </CardBox>
  );
};

EditMediaCard.propTypes = {
  className: PropTypes.string,
  media: PropTypes.object.isRequired,
  editClick: PropTypes.func.isRequired,
  mediaType: PropTypes.oneOf(['image', 'video']).isRequired,
  onDelete: PropTypes.func,
  loading: PropTypes.bool,
};
EditMediaCard.defaultProps = {
  loading: false,
  className: '',
};

export default memo(EditMediaCard);
